/**
 * A HOME DA CENTRAL — o que era e o que passou a ser.
 *
 * O QUE ERA: um `useState` com uma rede inventada dentro deste arquivo —
 * "Number One Restaurante", "Pizzaria Alfredo", "Loja Number One - Centro",
 * "Rua das Flores, 123", `salesToday: 15420.5`, `customers: 89`. Havia até um
 * `setInterval` de 10 s somando `Math.random() * 50` às vendas, para o número
 * parecer vivo. Nada disso existia no banco: a tela era uma maquete.
 *
 * O QUE É AGORA: `GET /painel/rede`. Cadastro (organização, companies,
 * unidades, endereço, telefone, coordenada) vem de `acd`; número do dia vem de
 * `aod`, pelo MESMO caminho do relatório de vendas — inclusive a reconstituição
 * do total, que o OPS não grava.
 *
 * A REGRA QUE MANDA AQUI: onde não há dado, a tela DIZ que não há. Uma unidade
 * cadastrada que ainda não opera no Alfredo mostra "sem operação", não
 * "R$ 0,00" — zero é um resultado, "não medido" é outro, e confundir os dois é
 * o que fazia esta tela mentir.
 */
import React, {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import * as AlfredoStyles from "../styles/components/Welcome";
import { useCombinedStore, useRedeStore } from "../store";
import { useDecodedToken } from "../services/jwt-decode.service";
import type { PinoDeLoja } from "./MapaDasLojas";
import {
  comoDinheiro,
  unidadesDoPainel,
  type UnidadeDoPainel,
} from "../services/painel.service";

/*
 * O MAPA ENTRA POR `lazy`, e o motivo é medido: o MapLibre sozinho engorda o
 * bundle principal em ~280 kB comprimidos (o `npm run build` foi de 436 kB para
 * 716 kB quando ele entrou de forma estática). Só a Home usa mapa; quem abre a
 * Central para ver um relatório não deve baixar um motor de WebGL para isso.
 */
const MapaDasLojas = lazy(() => import("./MapaDasLojas"));

/** O estado que a bolinha e a etiqueta mostram, derivado do dado — não inventado. */
type EstadoDaUnidade = "online" | "warning" | "offline";

function estadoDaUnidade(unidade: UnidadeDoPainel): EstadoDaUnidade {
  if (!unidade.operaNoOps) return "offline";
  return unidade.aceitaPedido ? "online" : "warning";
}

function rotuloDoEstado(estado: EstadoDaUnidade): string {
  if (estado === "online") return "Operando";
  if (estado === "warning") return "Sem pedido online";
  return "Sem operação";
}

const Welcome: React.FC = () => {
  const navigate = useNavigate();
  const { authUserZustand } = useCombinedStore();
  const decryptedToken: any = useDecodedToken(authUserZustand?.Token);

  // A rede vem do store COMPARTILHADO com a barra da direita. Cada uma
  // buscando por conta própria daria duas requisições e, pior, duas verdades:
  // era exatamente esse o defeito da versão anterior, em que os dois lados
  // tinham listas de lojas diferentes escritas à mão.
  const {
    painel,
    carregando,
    erro,
    unidadeSelecionada: selecionada,
    selecionarUnidade,
    carregarRede,
  } = useRedeStore();
  const [mostrarSeletor, setMostrarSeletor] = useState(false);
  const [saudacao, setSaudacao] = useState("");
  const [nomeDoUsuario, setNomeDoUsuario] = useState<string>("");

  useEffect(() => {
    if (!decryptedToken) return;
    const info = decryptedToken.userInfo ?? {};
    setNomeDoUsuario(info.userSocialName || info.userName || "");
  }, [decryptedToken]);

  useEffect(() => {
    const calcular = () => {
      const hora = new Date().getHours();
      if (hora >= 5 && hora < 12) return "Bom dia";
      if (hora >= 12 && hora < 18) return "Boa tarde";
      return "Boa noite";
    };
    setSaudacao(calcular());
    const intervalo = setInterval(() => setSaudacao(calcular()), 60000);
    return () => clearInterval(intervalo);
  }, []);

  useEffect(() => {
    if (!painel && !carregando && !erro) void carregarRede();
  }, [painel, carregando, erro, carregarRede]);

  const unidades = useMemo(() => unidadesDoPainel(painel), [painel]);

  const unidade = useMemo(
    () => unidades.find((u) => u.uuid === selecionada) ?? null,
    [unidades, selecionada],
  );

  const pinos: PinoDeLoja[] = useMemo(
    () =>
      unidades.map((u) => ({
        uuid: u.uuid,
        nome: u.nome,
        lat: u.lat,
        lng: u.lng,
        operando: u.operaNoOps,
      })),
    [unidades],
  );

  const selecionar = useCallback(
    (uuid: string) => {
      selecionarUnidade(uuid);
      setMostrarSeletor(false);
    },
    [selecionarUnidade],
  );

  const ligar = (telefone: string | null, evento: React.MouseEvent) => {
    evento.stopPropagation();
    if (!telefone) return;
    window.open(`tel:${telefone.replace(/\D/g, "")}`, "_self");
  };

  const whatsapp = (
    numero: string | null,
    nomeDaLoja: string,
    evento: React.MouseEvent,
  ) => {
    evento.stopPropagation();
    if (!numero) return;
    const texto = `Olá, gostaria de mais informações sobre a loja ${nomeDaLoja}.`;
    window.open(
      `https://wa.me/55${numero.replace(/\D/g, "")}?text=${encodeURIComponent(texto)}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  const estado = unidade ? estadoDaUnidade(unidade) : "offline";

  return (
    <AlfredoStyles.WelcomeContainer>
      <AlfredoStyles.MainCard>
        <AlfredoStyles.WelcomeHeader>
          <AlfredoStyles.Icon>nights_stay</AlfredoStyles.Icon>
          <AlfredoStyles.GreetingContainer>
            <AlfredoStyles.Greeting>
              {saudacao}
              {nomeDoUsuario ? ", " : ""}
              <AlfredoStyles.UserName>{nomeDoUsuario}</AlfredoStyles.UserName>
            </AlfredoStyles.Greeting>
            {painel?.organizacao && (
              <AlfredoStyles.StoreAddress as="div">
                {painel.organizacao.nome} · {unidades.length}{" "}
                {unidades.length === 1 ? "unidade" : "unidades"}
              </AlfredoStyles.StoreAddress>
            )}
          </AlfredoStyles.GreetingContainer>
        </AlfredoStyles.WelcomeHeader>

        <AlfredoStyles.MapSection>
          <Suspense
            fallback={
              <AlfredoStyles.EstadoDaRede role="status">
                Carregando o mapa…
              </AlfredoStyles.EstadoDaRede>
            }
          >
            <MapaDasLojas
              lojas={pinos}
              selecionada={selecionada}
              onSelecionar={selecionar}
            />
          </Suspense>
        </AlfredoStyles.MapSection>

        {carregando && (
          <AlfredoStyles.EstadoDaRede role="status">
            Carregando a rede…
          </AlfredoStyles.EstadoDaRede>
        )}

        {!carregando && erro && (
          <AlfredoStyles.EstadoDaRede role="alert" $erro>
            {erro}
          </AlfredoStyles.EstadoDaRede>
        )}

        {!carregando && !erro && unidades.length === 0 && (
          <AlfredoStyles.EstadoDaRede role="status">
            Nenhuma unidade cadastrada nesta organização ainda.
          </AlfredoStyles.EstadoDaRede>
        )}

        {unidade && (
          <AlfredoStyles.StoresList>
            <AlfredoStyles.StoreItem selected>
              <AlfredoStyles.StoreStatus status={estado} />
              <AlfredoStyles.StoreInfo>
                <AlfredoStyles.StoreHeader>
                  <AlfredoStyles.StoreName>{unidade.nome}</AlfredoStyles.StoreName>
                  <AlfredoStyles.StoreHeaderRight>
                    <AlfredoStyles.StoreStatusBadge status={estado}>
                      {rotuloDoEstado(estado)}
                    </AlfredoStyles.StoreStatusBadge>
                    <AlfredoStyles.NavButton
                      type="button"
                      aria-expanded={mostrarSeletor}
                      aria-label="Escolher outra unidade"
                      onClick={() => setMostrarSeletor((antes) => !antes)}
                    >
                      <span className="material-icons">
                        {mostrarSeletor ? "expand_less" : "expand_more"}
                      </span>
                    </AlfredoStyles.NavButton>
                  </AlfredoStyles.StoreHeaderRight>
                </AlfredoStyles.StoreHeader>

                <AlfredoStyles.StoreAddress>
                  {unidade.endereco ?? "Endereço não cadastrado"}
                </AlfredoStyles.StoreAddress>

                <AlfredoStyles.StoreCompactRow>
                  <AlfredoStyles.StoreMetricsCompact>
                    <AlfredoStyles.MetricCompact>
                      <AlfredoStyles.MetricLabel>Vendas</AlfredoStyles.MetricLabel>
                      <AlfredoStyles.MetricValue $tom="verde">
                        {unidade.numeros
                          ? comoDinheiro(unidade.numeros.receitaLiquida)
                          : "—"}
                      </AlfredoStyles.MetricValue>
                    </AlfredoStyles.MetricCompact>
                    <AlfredoStyles.MetricCompact>
                      <AlfredoStyles.MetricLabel>Pedidos</AlfredoStyles.MetricLabel>
                      <AlfredoStyles.MetricValue $tom="azul">
                        {unidade.numeros ? unidade.numeros.pedidos : "—"}
                      </AlfredoStyles.MetricValue>
                    </AlfredoStyles.MetricCompact>
                    <AlfredoStyles.MetricCompact>
                      <AlfredoStyles.MetricLabel>Pessoas</AlfredoStyles.MetricLabel>
                      <AlfredoStyles.MetricValue $tom="azul">
                        {unidade.numeros ? unidade.numeros.pessoas : "—"}
                      </AlfredoStyles.MetricValue>
                    </AlfredoStyles.MetricCompact>
                    <AlfredoStyles.MetricCompact>
                      <AlfredoStyles.MetricLabel>Ticket</AlfredoStyles.MetricLabel>
                      <AlfredoStyles.MetricValue $tom="ambar">
                        {unidade.numeros?.ticket
                          ? comoDinheiro(unidade.numeros.ticket)
                          : "—"}
                      </AlfredoStyles.MetricValue>
                    </AlfredoStyles.MetricCompact>
                  </AlfredoStyles.StoreMetricsCompact>

                  <AlfredoStyles.StoreActionsCompact>
                    <AlfredoStyles.ActionButtonCompact
                      type="button"
                      disabled={!unidade.telefone}
                      onClick={(e) => ligar(unidade.telefone, e)}
                    >
                      <span className="material-icons">call</span>
                      Ligar
                    </AlfredoStyles.ActionButtonCompact>
                    <AlfredoStyles.ActionButtonCompact
                      type="button"
                      $whatsapp
                      disabled={!(unidade.whatsapp || unidade.telefone)}
                      onClick={(e) =>
                        whatsapp(unidade.whatsapp || unidade.telefone, unidade.nome, e)
                      }
                    >
                      <span className="material-icons">chat</span>
                      WhatsApp
                    </AlfredoStyles.ActionButtonCompact>
                    <AlfredoStyles.ActionButtonCompact
                      type="button"
                      $primary
                      onClick={() => navigate("/settings?aba=Lojas")}
                    >
                      <span className="material-icons">tune</span>
                      Detalhes
                    </AlfredoStyles.ActionButtonCompact>
                  </AlfredoStyles.StoreActionsCompact>
                </AlfredoStyles.StoreCompactRow>

                {/* A frase que impede a tela de mentir com um zero. */}
                {!unidade.numeros && (
                  <AlfredoStyles.LastUpdate>
                    Esta unidade está cadastrada no Central mas ainda não tem
                    operação no Alfredo — não há caixa nem pedido para medir.
                  </AlfredoStyles.LastUpdate>
                )}
                {unidade.numeros && unidade.atribuicao === "rede" && (
                  <AlfredoStyles.LastUpdate>
                    Este número é o da REDE INTEIRA no período: o OPS ainda não
                    separa venda por unidade.
                  </AlfredoStyles.LastUpdate>
                )}
                {unidade.numeros && unidade.atribuicao === "unidade" && (
                  <AlfredoStyles.LastUpdate>
                    Período: {painel?.periodo.de}
                    {painel && painel.periodo.ate !== painel.periodo.de
                      ? ` a ${painel.periodo.ate}`
                      : ""}
                    .
                  </AlfredoStyles.LastUpdate>
                )}

                {mostrarSeletor && painel && (
                  <AlfredoStyles.StoreSelectorPanel>
                    {painel.companies.map((company) => (
                      <div key={company.uuid}>
                        <AlfredoStyles.EstablishmentHeader>
                          <AlfredoStyles.EstablishmentIcon className="material-icons">
                            storefront
                          </AlfredoStyles.EstablishmentIcon>
                          <AlfredoStyles.EstablishmentName>
                            {company.nome}
                          </AlfredoStyles.EstablishmentName>
                          <AlfredoStyles.EstablishmentBadge>
                            {company.unidades.length}
                          </AlfredoStyles.EstablishmentBadge>
                        </AlfredoStyles.EstablishmentHeader>
                        {company.unidades.map((item) => (
                          <AlfredoStyles.StoreSelectorItem
                            key={item.uuid}
                            selected={item.uuid === selecionada}
                            onClick={() => selecionar(item.uuid)}
                          >
                            <AlfredoStyles.StoreStatus
                              status={estadoDaUnidade(item)}
                            />
                            <div>
                              <AlfredoStyles.StoreName>
                                {item.nome}
                              </AlfredoStyles.StoreName>
                              <AlfredoStyles.StoreAddress>
                                {item.endereco?.split(",")[0] ??
                                  "Endereço não cadastrado"}
                              </AlfredoStyles.StoreAddress>
                            </div>
                          </AlfredoStyles.StoreSelectorItem>
                        ))}
                      </div>
                    ))}
                  </AlfredoStyles.StoreSelectorPanel>
                )}
              </AlfredoStyles.StoreInfo>
            </AlfredoStyles.StoreItem>
          </AlfredoStyles.StoresList>
        )}
      </AlfredoStyles.MainCard>
    </AlfredoStyles.WelcomeContainer>
  );
};

export default Welcome;
